import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import type { SidebarSection } from "@/app-shell";
import { useGetBusiness } from "@/business/query";
import { useBusiness } from "@/core/business";
import { FEATURE_FLAG_NAMES } from "@/core/feature-flags/feature-flag-name";
import { useFeatureFlag } from "@/core/feature-flags/use-feature-flag";
import { useGetMyUser } from "@/user/query";
import { ownersWriteTemplates } from "./access";
import {
  agentList,
  agentRoute,
  columnAgents,
  jobCostingHome,
  loadingRows,
  newAgent,
  openAgentPages,
} from "./job-costing-nav";
import { openRunPages, recentRuns, runRoute } from "./job-costing-runs";
import {
  cachedPeriodOutput,
  latestCachedOverview,
  useReportOverview,
} from "./overview/query";
import { useReportHistory, useReportOutput, useReportRun } from "./query";
import { useReport, useReports } from "./report-query";
import { openFindingCount, useFindings } from "./review/query";
import {
  canManageProfitability,
  canOpenProfitabilityReports,
  isProfitabilityPath,
  profitabilityRunPath,
} from "./routes";
import {
  useAgentExpansion,
  useExpandedAgentPages,
  usePrefetchAgentPages,
} from "./use-expanded-agents";

const RECENT_RUNS = 5;

/**
 * Job Costing's column. In documents mode: the open agent's pages for the
 * period on screen, then every agent. In ServiceTitan mode: the open run's
 * tabs, then the latest runs. Each list loads with the queries its page
 * already makes, so the column adds no requests of its own beyond the lists.
 */
export const useJobCostingNav = (): SidebarSection[] => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { businessId } = useBusiness();
  const { data: user } = useGetMyUser();
  const { data: business } = useGetBusiness(businessId);
  const { enabled: documentsOnly, isLoading: loadingFlag } = useFeatureFlag(
    FEATURE_FLAG_NAMES.profitability_documents_only,
  );
  const canOpen = canOpenProfitabilityReports(user, business, documentsOnly);
  const id = canOpen ? businessId : null;
  const bid = businessId ?? "";
  const agentsMode = canOpen && documentsOnly;
  const legacyMode = canOpen && !documentsOnly;

  const agents = useReports(agentsMode ? id : null);
  const route = agentsMode ? agentRoute(pathname) : null;
  const reportId = route?.reportId ?? "";
  // The list holds active agents only; an archived one still opens on its
  // own page (useTemplateReport reads it by id), so it is read the same way.
  const listed = agents.data?.find((agent) => agent.id === reportId);
  const byId = useReport(
    id,
    reportId,
    !!reportId && (agents.isSuccess || agents.isError) && !listed,
  );
  const expansion = useAgentExpansion(reportId);
  const prefetchAgent = usePrefetchAgentPages(bid);
  const openedPages = useExpandedAgentPages(
    bid,
    agentsMode ? expansion.opened : [],
  );
  const params = new URLSearchParams(search);
  const periodId = params.get("period");
  const client = useQueryClient();
  // While a period loads, the agent's latest overview and output already in
  // the cache stand in for it, so its rows stay put rather than empty out.
  const overview = useReportOverview(
    bid,
    reportId,
    periodId,
    params.get("compare"),
    !!reportId,
    () => latestCachedOverview(client, bid, reportId),
  );
  const findings = useFindings(
    bid,
    reportId,
    "open",
    periodId ? { runId: periodId } : undefined,
    { enabled: !!reportId },
  );
  const period = overview.data?.run ?? null;
  const periodRow = overview.data?.periods?.find(
    (item) => item.run_id === period?.id,
  );
  const listedCount = openFindingCount(findings.data?.pages[0]?.counts);
  const output = useReportOutput(
    bid,
    period?.interactive_available ? period.id : "",
    () => cachedPeriodOutput(client, bid, reportId),
  );

  const runMatch = canOpen ? runRoute(pathname) : null;
  const run = useReportRun(id, runMatch?.runId);
  // A version 2 run belongs to its agent; only ServiceTitan's are listed.
  const legacyRun = run.data?.workflow_version === 1 ? run.data : null;
  const runOutput = useReportOutput(
    bid,
    legacyRun?.interactive_available ? legacyRun.id : "",
  );
  const history = useReportHistory(legacyMode ? id : null, undefined, 1);
  const runExpansion = useAgentExpansion(legacyRun?.id ?? "");

  // Until who the user is and which mode the business runs are known, hold
  // the column's place on a Job Costing page so the page does not jump right
  // when the rows arrive, nor show one mode's rows before the other's.
  if (loadingFlag || !user || (businessId && !business))
    return isProfitabilityPath(pathname) ? loadingRows() : [];
  if (!canOpen) return [];

  const authoring =
    agentsMode &&
    business?.profitability_access !== "view" &&
    ownersWriteTemplates(business);
  const sections: SidebarSection[] = [
    jobCostingHome(pathname, agentsMode ? "All agents" : "Reports"),
    ...(authoring ? [newAgent(pathname)] : []),
  ];
  const report = listed ?? byId.data ?? undefined;
  if (agentsMode) {
    const pages =
      route && report?.published
        ? openAgentPages({
            reportId,
            section: route.section,
            pane: route.pane,
            search,
            overview: {
              data: overview.data,
              pending: overview.isPending,
              stale: overview.isPlaceholderData,
            },
            output: {
              data: output.data,
              pending: output.isPending,
              stale: output.isPlaceholderData,
            },
            activeCount: findings.isSuccess
              ? listedCount
              : (periodRow?.active_findings ?? 0),
            findingsPending: !findings.isSuccess && periodRow === undefined,
          })
        : undefined;
    sections.push(
      ...agentList({
        agents: columnAgents({
          loaded: agents.data,
          failed: agents.isError,
          report: report && { id: report.id, name: report.name },
          listed: !!listed,
        }),
        currentId: route ? reportId : null,
        settings: route?.section === "settings",
        pages: route ? { ...openedPages, [reportId]: pages } : openedPages,
        isExpanded: expansion.isExpanded,
        onExpandedChange: expansion.setExpanded,
        onIntent: prefetchAgent,
      }),
    );
  }
  // As the Job Costing page's menu offers it.
  const historyListed =
    agentsMode &&
    canManageProfitability(user) &&
    business?.profitability_access !== "view" &&
    !!business?.profitability_workflows?.includes(1);
  // An open ServiceTitan run is always listed: its page has no tab strip on
  // desktop, so the column is the only way between its tabs.
  if (legacyMode || historyListed || legacyRun)
    sections.push(
      ...recentRuns({
        runs: legacyMode
          ? history.isError
            ? []
            : history.data?.pages[0]?.items
                .slice(0, RECENT_RUNS)
                .map((item) => ({ id: item.id, label: item.display.period }))
          : undefined,
        open: legacyRun
          ? {
              id: legacyRun.id,
              label: legacyRun.display.period,
              pages: openRunPages({
                runId: legacyRun.id,
                interactive: legacyRun.interactive_available,
                pending: runOutput.isPending,
                output: runOutput.data,
                sheet: legacyRun.workbook_available,
                documents: !!legacyRun.documents?.length,
                current: runMatch?.pane ?? null,
              }),
            }
          : null,
        pathname,
        history: agentsMode,
        historyRow: historyListed,
        isExpanded: runExpansion.isExpanded,
        onExpandedChange: (runId, expanded) => {
          // A closed run's chevron opens that run. The one on screen folds.
          if (runId !== legacyRun?.id) {
            if (expanded) void navigate(profitabilityRunPath(runId));
            return;
          }
          runExpansion.setExpanded(runId, expanded);
        },
      }),
    );
  return sections;
};
